import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { updateUserKyc, uploadKycDocument } from '../../utils/api';
import styles from './KycPage.module.css';
import { useAuth } from '../../context/AuthContext';
import { useProgressBar } from '../../context/ProgressBarContext';
import RegistrationSteps from '../../components/RegistrationSteps';
import MessageBox from '../../components/MessageBox';
import { FiUpload } from 'react-icons/fi';

function KycPage() {
  const [formData, setFormData] = useState({
    aadhaarCard: '',
    panCard: '',
    dateOfBirth: '',
  });
  const [aadhaarFrontFile, setAadhaarFrontFile] = useState(null);
  const [aadhaarBackFile, setAadhaarBackFile] = useState(null);
  const [panFrontFile, setPanFrontFile] = useState(null);
  const [panBackFile, setPanBackFile] = useState(null);

  const [aadhaarFrontFileName, setAadhaarFrontFileName] = useState('Choose front side');
  const [aadhaarBackFileName, setAadhaarBackFileName] = useState('Choose back side');
  const [panFrontFileName, setPanFrontFileName] = useState('Choose front side');
  const [panBackFileName, setPanBackFileName] = useState('Choose back side');

  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState(null);
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const { showProgressBar, updateProgressBar, hideProgressBar } = useProgressBar();

  useEffect(() => {
    if (user) {
      setFormData({
        aadhaarCard: user.aadhaarNumber || '',
        panCard: user.panNumber || '',
        dateOfBirth: user.dateOfBirth || '',
      });
      if (user.aadhaarFrontCard || user.aadhaarCard) {
        setAadhaarFrontFileName('Uploaded (Click to change)');
      } else {
        setAadhaarFrontFileName('Choose front side');
      }
      if (user.aadhaarBackCard) {
        setAadhaarBackFileName('Uploaded (Click to change)');
      } else {
        setAadhaarBackFileName('Choose back side');
      }
      if (user.panFrontCard || user.panCard) {
        setPanFrontFileName('Uploaded (Click to change)');
      } else {
        setPanFrontFileName('Choose front side');
      }
      if (user.panBackCard) {
        setPanBackFileName('Uploaded (Click to change)');
      } else {
        setPanBackFileName('Choose back side');
      }
    }
  }, [user]);

  const aadhaarFrontFileRef = useRef(null);
  const aadhaarBackFileRef = useRef(null);
  const panFrontFileRef = useRef(null);
  const panBackFileRef = useRef(null);

  const { aadhaarCard, panCard, dateOfBirth } = formData;

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'aadhaarCard') {
      const numericValue = value.replace(/\D/g, '');
      const truncatedValue = numericValue.slice(0, 12);
      const formattedValue = truncatedValue.replace(/(\d{4})(?=\d)/g, '$1-');
      setFormData({ ...formData, [name]: formattedValue });
    } else if (name === 'panCard') {
      const uppercaseValue = value.toUpperCase();
      let formattedValue = '';
      for (let i = 0; i < uppercaseValue.length && i < 10; i++) {
        const char = uppercaseValue[i];
        if (i < 5) {
          if (/[A-Z]/.test(char)) formattedValue += char;
        } else if (i < 9) {
          if (/[0-9]/.test(char)) formattedValue += char;
        } else {
          if (/[A-Z]/.test(char)) formattedValue += char;
        }
      }
      setFormData({ ...formData, [name]: formattedValue });
    } else if (name === 'dateOfBirth') {
      let formattedDate = value.replace(/\D/g, '');
      if (formattedDate.length > 2)
        formattedDate = formattedDate.slice(0, 2) + '/' + formattedDate.slice(2);
      if (formattedDate.length > 5)
        formattedDate = formattedDate.slice(0, 5) + '/' + formattedDate.slice(5);
      if (formattedDate.length > 10)
        formattedDate = formattedDate.slice(0, 10);
      setFormData({ ...formData, [name]: formattedDate });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const selectedFile = files.length > 0 ? files[0] : null;

    if (name === 'aadhaarFrontFile') {
      setAadhaarFrontFile(selectedFile);
      setAadhaarFrontFileName(selectedFile ? selectedFile.name : 'Choose front side');
    } else if (name === 'aadhaarBackFile') {
      setAadhaarBackFile(selectedFile);
      setAadhaarBackFileName(selectedFile ? selectedFile.name : 'Choose back side');
    } else if (name === 'panFrontFile') {
      setPanFrontFile(selectedFile);
      setPanFrontFileName(selectedFile ? selectedFile.name : 'Choose front side');
    } else if (name === 'panBackFile') {
      setPanBackFile(selectedFile);
      setPanBackFileName(selectedFile ? selectedFile.name : 'Choose back side');
    }
  };

  const handleFileButtonClick = (ref) => {
    ref.current.click();
  };

  const validate = () => {
    let tempErrors = {};
    if (!aadhaarCard.replace(/-/g, '').match(/^[0-9]{12}$/))
      tempErrors.aadhaarCard = 'Aadhaar card must be 12 digits.';
    if (!panCard.match(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/))
      tempErrors.panCard = 'PAN card is not valid.';
    if (!dateOfBirth.match(/^\d{2}\/\d{2}\/\d{4}$/))
      tempErrors.dateOfBirth = 'Date of birth must be in DD/MM/YYYY format.';
    if (user && user.kycStatus === 'not submitted') {
      if (!aadhaarFrontFile && !user?.aadhaarFrontCard && !user?.aadhaarCard) {
        tempErrors.aadhaarFrontFile = 'Aadhaar front file is required.';
      }
      if (!aadhaarBackFile && !user?.aadhaarBackCard) {
        tempErrors.aadhaarBackFile = 'Aadhaar back file is required.';
      }
      if (!panFrontFile && !user?.panFrontCard && !user?.panCard) {
        tempErrors.panFrontFile = 'PAN front file is required.';
      }
      if (!panBackFile && !user?.panBackCard) {
        tempErrors.panBackFile = 'PAN back file is required.';
      }
    }
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const navigateToNextStep = (updatedUser) => {
    if (updatedUser.additionalDetailsStatus === 'not submitted') {
      navigate('/additional-details');
    } else if (updatedUser.bankStatus === 'not submitted') {
      navigate('/bank-details');
    } else {
      navigate('/account-status');
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage(null);
    if (validate()) {
      showProgressBar();
      updateProgressBar(30);

      const kycDetails = {
        aadhaarNumber: aadhaarCard.replace(/-/g, ''),
        panNumber: panCard,
        dateOfBirth,
      };

      try {
        await new Promise(resolve => setTimeout(resolve, 250));
        updateProgressBar(60);

        const { data: updatedUser } = await updateUserKyc(kycDetails);
        updateUser(updatedUser);

        if (aadhaarFrontFile) {
          const aadhaarFrontFormData = new FormData();
          aadhaarFrontFormData.append('documentType', 'aadhaar_front');
          aadhaarFrontFormData.append('document', aadhaarFrontFile);
          await uploadKycDocument(aadhaarFrontFormData);
        }

        if (aadhaarBackFile) {
          const aadhaarBackFormData = new FormData();
          aadhaarBackFormData.append('documentType', 'aadhaar_back');
          aadhaarBackFormData.append('document', aadhaarBackFile);
          await uploadKycDocument(aadhaarBackFormData);
        }

        if (panFrontFile) {
          const panFrontFormData = new FormData();
          panFrontFormData.append('documentType', 'pan_front');
          panFrontFormData.append('document', panFrontFile);
          await uploadKycDocument(panFrontFormData);
        }

        if (panBackFile) {
          const panBackFormData = new FormData();
          panBackFormData.append('documentType', 'pan_back');
          panBackFormData.append('document', panBackFile);
          await uploadKycDocument(panBackFormData);
        }

        updateProgressBar(90);
        await new Promise(resolve => setTimeout(resolve, 250));

        navigateToNextStep(updatedUser);
      } catch (err) {
        setErrorMessage(err.response?.data?.msg || 'An unexpected error occurred. Please try again.');
      } finally {
        hideProgressBar();
      }
    }
  };

  return (
    <div className={styles.kycPageWrapper}>
      {errorMessage && <MessageBox message={errorMessage} type="error" onClose={() => setErrorMessage(null)} />}

      <RegistrationSteps currentStep={2} />

      <div className={styles.kycContainer}>
        <div className={styles.kycFormSection}>
          <div className={styles.formHeader}>
            <h2>Identity Verification</h2>
            <p>Step 2: KYC Details</p>
          </div>

          <form onSubmit={onSubmit}>
            <div className={styles.formGroup}>
              <label htmlFor="aadhaarCard">Aadhaar Card Number</label>
              <input
                type="text"
                id="aadhaarCard"
                name="aadhaarCard"
                value={aadhaarCard}
                onChange={handleChange}
                inputMode="numeric"
                placeholder="0000-0000-0000"
              />
              {errors.aadhaarCard && <p className={styles.errorText}>{errors.aadhaarCard}</p>}

              <label className={styles.uploadSectionTitle}>Please upload clear image of Aadhaar Card</label>
              <div className={styles.uploadRow}>
                <div className={styles.uploadSubGroup}>
                  <input
                    type="file"
                    id="aadhaarFrontFile"
                    name="aadhaarFrontFile"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={handleFileChange}
                    className={styles.fileInputHidden}
                    ref={aadhaarFrontFileRef}
                  />
                  <div className={styles.uploadBox} onClick={() => handleFileButtonClick(aadhaarFrontFileRef)}>
                    <FiUpload className={styles.uploadIcon} />
                    <span className={styles.fileName}>{aadhaarFrontFileName}</span>
                  </div>
                  {errors.aadhaarFrontFile && <p className={styles.errorText}>{errors.aadhaarFrontFile}</p>}
                </div>

                <div className={styles.uploadSubGroup}>
                  <input
                    type="file"
                    id="aadhaarBackFile"
                    name="aadhaarBackFile"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={handleFileChange}
                    className={styles.fileInputHidden}
                    ref={aadhaarBackFileRef}
                  />
                  <div className={styles.uploadBox} onClick={() => handleFileButtonClick(aadhaarBackFileRef)}>
                    <FiUpload className={styles.uploadIcon} />
                    <span className={styles.fileName}>{aadhaarBackFileName}</span>
                  </div>
                  {errors.aadhaarBackFile && <p className={styles.errorText}>{errors.aadhaarBackFile}</p>}
                </div>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="panCard">PAN Card Number</label>
              <input
                type="text"
                id="panCard"
                name="panCard"
                value={panCard}
                onChange={handleChange}
                placeholder="ABCDE1234F"
              />
              {errors.panCard && <p className={styles.errorText}>{errors.panCard}</p>}

              <label className={styles.uploadSectionTitle}>Please upload clear image of your PAN card</label>
              <div className={styles.uploadRow}>
                <div className={styles.uploadSubGroup}>
                  <input
                    type="file"
                    id="panFrontFile"
                    name="panFrontFile"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={handleFileChange}
                    className={styles.fileInputHidden}
                    ref={panFrontFileRef}
                  />
                  <div className={styles.uploadBox} onClick={() => handleFileButtonClick(panFrontFileRef)}>
                    <FiUpload className={styles.uploadIcon} />
                    <span className={styles.fileName}>{panFrontFileName}</span>
                  </div>
                  {errors.panFrontFile && <p className={styles.errorText}>{errors.panFrontFile}</p>}
                </div>

                <div className={styles.uploadSubGroup}>
                  <input
                    type="file"
                    id="panBackFile"
                    name="panBackFile"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={handleFileChange}
                    className={styles.fileInputHidden}
                    ref={panBackFileRef}
                  />
                  <div className={styles.uploadBox} onClick={() => handleFileButtonClick(panBackFileRef)}>
                    <FiUpload className={styles.uploadIcon} />
                    <span className={styles.fileName}>{panBackFileName}</span>
                  </div>
                  {errors.panBackFile && <p className={styles.errorText}>{errors.panBackFile}</p>}
                </div>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="dateOfBirth">Date of Birth</label>
              <input
                type="text"
                id="dateOfBirth"
                name="dateOfBirth"
                value={dateOfBirth}
                onChange={handleChange}
                placeholder="DD/MM/YYYY"
                maxLength="10"
              />
              {errors.dateOfBirth && <p className={styles.errorText}>{errors.dateOfBirth}</p>}
            </div>

            <button type="submit" className={styles.btnSubmit}>Continue</button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default KycPage;